import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import { applyTheme, readStoredTheme } from '../theme';
import { BackIcon, GlobeIcon } from './icons';
import coverageMarkdown from '../coverage.generated.md?raw';
import './CoveragePage.css';

// The doc is written for a general/compliance audience; its own "Technical
// reference (engineers)" section is split off and collapsed so non-technical
// readers aren't met with adapter internals (it stays one click away). Matched
// as a line-anchored top-level heading so an inline mention of the phrase can't
// mis-split the doc.
const ENGINEER_HEADING_RE = /^## Technical reference \(engineers\)\s*$/m;
const ENGINEER_ANCHOR = 'technical-reference-engineers';

export function CoveragePage() {
  const engineerRef = useRef<HTMLDetailsElement>(null);

  const openEngineer = () => {
    if (engineerRef.current) engineerRef.current.open = true;
  };

  // The main App owns theme reactivity; a direct deep-link/refresh to /coverage
  // never mounts it, so apply the stored theme here to render in the right mode.
  useEffect(() => {
    applyTheme(readStoredTheme());
  }, []);

  // The intro links to "#technical-reference-engineers"; expand the (collapsed)
  // engineer section when that anchor is targeted. Covers deep-link arrival and
  // URL-bar/history hash changes; an in-page click is handled directly below
  // (a same-hash re-click fires no hashchange event).
  useEffect(() => {
    const openIfTargeted = () => {
      if (window.location.hash === `#${ENGINEER_ANCHOR}`) openEngineer();
    };
    openIfTargeted();
    window.addEventListener('hashchange', openIfTargeted);
    return () => window.removeEventListener('hashchange', openIfTargeted);
  }, []);

  const markdownComponents = useMemo<Components>(
    () => ({
      // External http(s) links open in a new tab. Relative repo paths (e.g.
      // docs/prd.md) don't resolve from the SPA, so render them as plain styled
      // text rather than dead links. In-page anchors (#…) resolve via rehype-slug;
      // the engineer anchor also force-opens the collapsed section on click.
      a: ({ href, children }) => {
        if (!href) return <>{children}</>;
        if (href.startsWith('#')) {
          const onClick = href === `#${ENGINEER_ANCHOR}` ? openEngineer : undefined;
          return (
            <a href={href} onClick={onClick}>
              {children}
            </a>
          );
        }
        if (href.startsWith('http://') || href.startsWith('https://')) {
          return (
            <a href={href} target="_blank" rel="noreferrer noopener">
              {children}
            </a>
          );
        }
        return <span className="coverage-deadlink">{children}</span>;
      },
      // Wrap wide matrices so they scroll horizontally instead of overflowing.
      table: ({ children }) => (
        <div className="coverage-table-wrap">
          <table>{children}</table>
        </div>
      ),
    }),
    [],
  );

  const [intro, engineer] = useMemo(() => {
    const match = ENGINEER_HEADING_RE.exec(coverageMarkdown);
    if (!match) return ['', coverageMarkdown] as const;
    const before = coverageMarkdown.slice(0, match.index).replace(/\n*-{3,}\s*$/, '').trimEnd();
    const after = coverageMarkdown.slice(match.index + match[0].length).trimStart();
    return [before, after] as const;
  }, []);

  return (
    <div className="coverage-page">
      <header className="coverage-bar">
        <Link to="/" className="coverage-back">
          <BackIcon />
          Back to search
        </Link>
        <span className="coverage-bar-title">
          <GlobeIcon />
          Coverage
        </span>
      </header>

      <main className="coverage-main">
        <article className="coverage-doc">
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]} components={markdownComponents}>
            {intro}
          </Markdown>

          {engineer ? (
            <details id={ENGINEER_ANCHOR} ref={engineerRef} className="coverage-engineer">
              <summary>Technical reference (engineers)</summary>
              <div className="coverage-engineer-body">
                <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]} components={markdownComponents}>
                  {engineer}
                </Markdown>
              </div>
            </details>
          ) : null}
        </article>

        <div className="coverage-foot">
          <Link to="/" className="coverage-back">
            <BackIcon />
            Back to search
          </Link>
        </div>
      </main>
    </div>
  );
}
